import { ElectronicsConfiguration } from "../models/electronicsConfiguration.ts";
import { Signal } from "../models/signal.ts";

const signalKey = (signal: Signal): string => signal.source_id ?? signal.id;

const isDeletedMechanicalSignal = (
  signal: Signal,
  validMechanicalSignalIds: Set<string>,
  previousMechanicalSignalIds: Set<string>,
): boolean =>
  (signal.source_type === "mechanical_testpoint" ||
    previousMechanicalSignalIds.has(signalKey(signal)) ||
    previousMechanicalSignalIds.has(signal.id)) &&
  !validMechanicalSignalIds.has(signalKey(signal)) &&
  !validMechanicalSignalIds.has(signal.id);

const resetCommunicationSignal = (
  signal: Signal,
  validMechanicalSignalIds: Set<string>,
  previousMechanicalSignalIds: Set<string>,
): Signal =>
  isDeletedMechanicalSignal(signal, validMechanicalSignalIds, previousMechanicalSignalIds)
    ? {
        ...signal,
        net: "",
        name: "",
        source_type: "communication",
        source_id: signal.id,
      }
    : signal;

export const pruneElectronicsReferences = (
  config: ElectronicsConfiguration,
  validMechanicalSignals: Signal[],
): ElectronicsConfiguration => {
  const validMechanicalSignalIds = new Set(validMechanicalSignals.map(signalKey));
  const previousMechanicalSignalIds = new Set(config.testpoints.flatMap((signal) => [signal.id, signalKey(signal)]));

  const customResistor = config.custom_signals.resistor.filter(
    (item) => !isDeletedMechanicalSignal(item.source_signal, validMechanicalSignalIds, previousMechanicalSignalIds),
  );
  const customSchottky = config.custom_signals.schottky.filter(
    (item) => !isDeletedMechanicalSignal(item.source_signal, validMechanicalSignalIds, previousMechanicalSignalIds),
  );
  const removedCustomSignalIds = new Set([
    ...config.custom_signals.resistor
      .filter((item) => !customResistor.some((kept) => kept.id === item.id))
      .map((item) => item.id),
    ...config.custom_signals.schottky
      .filter((item) => !customSchottky.some((kept) => kept.id === item.id))
      .map((item) => item.id),
  ]);

  const isInvalidSignal = (signal: Signal): boolean =>
    isDeletedMechanicalSignal(signal, validMechanicalSignalIds, previousMechanicalSignalIds) ||
    removedCustomSignalIds.has(signal.id);

  return {
    ...config,
    testpoints: validMechanicalSignals,
    communication: {
      ...config.communication,
      rs232: {
        ...config.communication.rs232,
        rx: resetCommunicationSignal(config.communication.rs232.rx, validMechanicalSignalIds, previousMechanicalSignalIds),
        tx: resetCommunicationSignal(config.communication.rs232.tx, validMechanicalSignalIds, previousMechanicalSignalIds),
      },
      rs485: {
        ...config.communication.rs485,
        a: resetCommunicationSignal(config.communication.rs485.a, validMechanicalSignalIds, previousMechanicalSignalIds),
        b: resetCommunicationSignal(config.communication.rs485.b, validMechanicalSignalIds, previousMechanicalSignalIds),
      },
      uart: {
        ...config.communication.uart,
        rx: resetCommunicationSignal(config.communication.uart.rx, validMechanicalSignalIds, previousMechanicalSignalIds),
        tx: resetCommunicationSignal(config.communication.uart.tx, validMechanicalSignalIds, previousMechanicalSignalIds),
      },
      i2c0: {
        ...config.communication.i2c0,
        sda: resetCommunicationSignal(config.communication.i2c0.sda, validMechanicalSignalIds, previousMechanicalSignalIds),
        scl: resetCommunicationSignal(config.communication.i2c0.scl, validMechanicalSignalIds, previousMechanicalSignalIds),
      },
      i2c1: {
        ...config.communication.i2c1,
        sda: resetCommunicationSignal(config.communication.i2c1.sda, validMechanicalSignalIds, previousMechanicalSignalIds),
        scl: resetCommunicationSignal(config.communication.i2c1.scl, validMechanicalSignalIds, previousMechanicalSignalIds),
      },
      usb: {
        ...config.communication.usb,
        otg: resetCommunicationSignal(config.communication.usb.otg, validMechanicalSignalIds, previousMechanicalSignalIds),
        dp: resetCommunicationSignal(config.communication.usb.dp, validMechanicalSignalIds, previousMechanicalSignalIds),
        dn: resetCommunicationSignal(config.communication.usb.dn, validMechanicalSignalIds, previousMechanicalSignalIds),
      },
      sd_card: {
        ...config.communication.sd_card,
        clk: resetCommunicationSignal(config.communication.sd_card.clk, validMechanicalSignalIds, previousMechanicalSignalIds),
        d0: resetCommunicationSignal(config.communication.sd_card.d0, validMechanicalSignalIds, previousMechanicalSignalIds),
        d1: resetCommunicationSignal(config.communication.sd_card.d1, validMechanicalSignalIds, previousMechanicalSignalIds),
        d2: resetCommunicationSignal(config.communication.sd_card.d2, validMechanicalSignalIds, previousMechanicalSignalIds),
        d3: resetCommunicationSignal(config.communication.sd_card.d3, validMechanicalSignalIds, previousMechanicalSignalIds),
      },
    },
    flasher: {
      stlink1: {
        ...config.flasher.stlink1,
        target_voltage: resetCommunicationSignal(
          config.flasher.stlink1.target_voltage,
          validMechanicalSignalIds,
          previousMechanicalSignalIds,
        ),
        swdio_tms: resetCommunicationSignal(config.flasher.stlink1.swdio_tms, validMechanicalSignalIds, previousMechanicalSignalIds),
        swclk_tck: resetCommunicationSignal(config.flasher.stlink1.swclk_tck, validMechanicalSignalIds, previousMechanicalSignalIds),
        swo_tdo: resetCommunicationSignal(config.flasher.stlink1.swo_tdo, validMechanicalSignalIds, previousMechanicalSignalIds),
        tdi: resetCommunicationSignal(config.flasher.stlink1.tdi, validMechanicalSignalIds, previousMechanicalSignalIds),
        ntrst: resetCommunicationSignal(config.flasher.stlink1.ntrst, validMechanicalSignalIds, previousMechanicalSignalIds),
        nrst: resetCommunicationSignal(config.flasher.stlink1.nrst, validMechanicalSignalIds, previousMechanicalSignalIds),
      },
      stlink2: {
        ...config.flasher.stlink2,
        target_voltage: resetCommunicationSignal(
          config.flasher.stlink2.target_voltage,
          validMechanicalSignalIds,
          previousMechanicalSignalIds,
        ),
        swdio_tms: resetCommunicationSignal(config.flasher.stlink2.swdio_tms, validMechanicalSignalIds, previousMechanicalSignalIds),
        swclk_tck: resetCommunicationSignal(config.flasher.stlink2.swclk_tck, validMechanicalSignalIds, previousMechanicalSignalIds),
        swo_tdo: resetCommunicationSignal(config.flasher.stlink2.swo_tdo, validMechanicalSignalIds, previousMechanicalSignalIds),
        tdi: resetCommunicationSignal(config.flasher.stlink2.tdi, validMechanicalSignalIds, previousMechanicalSignalIds),
        ntrst: resetCommunicationSignal(config.flasher.stlink2.ntrst, validMechanicalSignalIds, previousMechanicalSignalIds),
        nrst: resetCommunicationSignal(config.flasher.stlink2.nrst, validMechanicalSignalIds, previousMechanicalSignalIds),
      },
    },
    custom_signals: {
      resistor: customResistor,
      schottky: customSchottky,
    },
    signal_mapping: {
      ...config.signal_mapping,
      relais: config.signal_mapping.relais.filter(
        (item) => !isInvalidSignal(item.high_signal) && !isInvalidSignal(item.low_signal),
      ),
      voltage: config.signal_mapping.voltage.filter((item) => !isInvalidSignal(item.signal)),
      current: config.signal_mapping.current.filter(
        (item) => !isInvalidSignal(item.high_signal) && !isInvalidSignal(item.low_signal),
      ),
      two_quadrant: config.signal_mapping.two_quadrant.filter((item) => !isInvalidSignal(item.signal)),
      resistor: config.signal_mapping.resistor.filter(
        (item) => !isInvalidSignal(item.pin1) && !isInvalidSignal(item.pin2),
      ),
    },
  };
};
